import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TabInsights from '../tabs/TabInsights';
import { apiGet } from '../utils/api-client';
import { categoryLabel } from '../mobile/utils/insights';

/**
 * TabInsightsContainer — branche TabInsights aux vrais insights backend.
 *
 * Mapping backend → composant :
 *  - category (Cuisine|Service|Cadre|Prix|Attente|Accessibilité) → emoji + label
 *  - type (alert|positive|neutral) → tag + tagColor + sentiment %
 *  - constat                    → summary (chiffré, actionnable)
 *  - verbatims                  → keywords (extraits courts)
 *  - action                     → champ custom (action recommandée)
 */

/** Mapping catégorie backend → emoji + couleur de défaut */
const CATEGORY_META = {
  Cuisine:        { emoji: '👨‍🍳', defaultScore: 4.4 },
  Service:        { emoji: '🍽️', defaultScore: 4.5 },
  Cadre:          { emoji: '🌿', defaultScore: 4.6 },
  Prix:           { emoji: '💰', defaultScore: 4.0 },
  Attente:        { emoji: '⏱️', defaultScore: 3.9 },
  Accessibilité:  { emoji: '♿', defaultScore: 4.2 },
  Ambiance:       { emoji: '🕯️', defaultScore: 4.7 },
};

/**
 * Sprint 2 P1 — mentions/sentiment sont désormais CALCULÉS côté backend
 * (Claude Haiku + sanitization stricte). Plus de hardcode SENTIMENT_BY_TYPE.
 * Si null/absent → on affiche "Données insuffisantes" plutôt qu'une fausse valeur.
 */

/** Tag + couleur selon le type. `t` est la fonction useTranslation('tabs'). */
function tagFor(type, t) {
  const ty = (type || '').toLowerCase();
  if (ty === 'positive' || ty === 'positif')  return { tag: t('insights.badge.force'), tagColor: 'green' };
  if (ty === 'alert' || ty === 'alerte' || ty === 'negatif' || ty === 'negative') return { tag: t('insights.badge.action_requise'), tagColor: 'red' };
  return { tag: t('insights.badge.stable'), tagColor: 'accent' };
}

export default function TabInsightsContainer() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('tabs');
  // Langue UI normalisée sur le code 2 lettres attendu par le backend (fr/en/es/it).
  const lang = (i18n.language || 'fr').split('-')[0];
  const [period, setPeriod]     = useState('30d');
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [realInsights, setRealInsights] = useState([]);
  const [trends, setTrends]     = useState([]);
  const [topKeywords, setTopKeywords] = useState([]);
  const [meta, setMeta]         = useState(null);

  // Chantier 3 — paywall : tier='free' (limited) gate les cartes 4+, l'`action`
  // des cartes 1-3, et masque trends + top_keywords. Le backend a déjà retiré
  // le contenu premium du payload (withholding) — on ajoute juste l'UI.
  const isFreeTier = meta?.tier === 'free';

  const loadAll = useCallback(async ({ force = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const insightsUrl = `/api/dashboard/insights?lang=${encodeURIComponent(lang)}${force ? '&force=true' : ''}`;
      const [dash, ins] = await Promise.all([
        apiGet('/api/dashboard/').catch(() => null),
        apiGet(insightsUrl).catch(() => null),
      ]);
      setRestaurant(dash?.restaurant || null);
      setRealInsights(Array.isArray(ins?.insights) ? ins.insights : []);
      setTrends(Array.isArray(ins?.trends) ? ins.trends : []);
      setTopKeywords(Array.isArray(ins?.top_keywords) ? ins.top_keywords : []);
      setMeta(ins?.meta || null);
    } catch (e) {
      console.error('[TabInsights] load error:', e);
      setError(e.message || t('insights.load_error'));
    } finally {
      setLoading(false);
    }
  }, [t, lang]); // `lang` en dep → l'useEffect se redéclenche au changement de drapeau

  useEffect(() => { loadAll(); }, [loadAll]);

  const handlePeriodChange = useCallback((newPeriod) => {
    setPeriod(newPeriod);
    loadAll();
  }, [loadAll]);

  const handleRefresh = useCallback(async () => { await loadAll({ force: true }); }, [loadAll]);

  const handleThemeClick = useCallback((themeId) => {
    console.log('[TabInsights] theme clicked:', themeId);
  }, []);

  /** Couleur + icône selon le type d'insight (carte hero) */
  const insightHeroStyle = (type) => {
    const ty = (type || '').toLowerCase();
    if (ty === 'positive' || ty === 'positif')   return { bg: '#F0FDF4', border: '#BBE5CC', tag: '#0A7A45', emoji: '💚', label: t('insights.badge.force') };
    if (ty === 'alert'    || ty === 'negatif')   return { bg: '#FEE7E1', border: '#FFCFC2', tag: '#DC2626', emoji: '⚠️', label: t('insights.badge.a_surveiller') };
    return { bg: '#FFF7ED', border: '#F8DAC9', tag: '#E8502A', emoji: '💡', label: t('insights.badge.conseil') };
  };

  /* ============= Mapping pour le composant TabInsights ============= */

  // Themes : grouper les insights par catégorie + ajouter les emojis
  // Sprint 2 P1 : mentions et sentiment viennent désormais du backend (calculés
  // sur les vrais avis). Si null → le composant TabInsights affiche "Données
  // insuffisantes" sur la barre de sentiment et masque le compteur de mentions.
  // Chantier 3 — paywall : on FILTRE les cartes locked du themes (rendu deeper).
  // Le bandeau récap + les cartes hero gèrent déjà l'incitation upgrade en haut ;
  // dupliquer dans TabInsights donnerait des "Données insuffisantes" partout pour
  // les locked (constat/verbatims absents) → bruit UX sans valeur.
  const visibleInsights = realInsights.filter(ins => !ins.locked);
  const themes = visibleInsights.length > 0
    ? visibleInsights.map((ins, i) => {
        const cat   = CATEGORY_META[ins.category] || { emoji: '💡', defaultScore: 4.0 };
        const tag   = tagFor(ins.type, t);
        const mentionsRaw  = (typeof ins.mentions === 'number' && Number.isFinite(ins.mentions) && ins.mentions >= 0)
          ? ins.mentions
          : null;
        const sentimentRaw = (typeof ins.sentiment === 'number' && Number.isFinite(ins.sentiment) && ins.sentiment >= 0 && ins.sentiment <= 100)
          ? ins.sentiment
          : null;
        return {
          id:        ins.id || `insight-${i}`,
          emoji:     cat.emoji,
          label:     categoryLabel(ins.category, t) || t('insights.fallback_insight_label'),
          score:     cat.defaultScore,
          sentiment: sentimentRaw,   // null si backend incertain → UI affiche placeholder
          mentions:  mentionsRaw,    // null si backend incertain → UI masque le compteur
          trend:     ins.type === 'positive' ? '+5%' : ins.type === 'alert' ? '−3%' : '0%',
          direction: ins.type === 'positive' ? 'up' : ins.type === 'alert' ? 'down' : 'flat',
          keywords:  Array.isArray(ins.verbatims)
            ? ins.verbatims.slice(0, 5).map(v => (v || '').slice(0, 30))
            : [],
          summary:   ins.constat || ins.description || ins.titre || '',
          tag:       tag.tag,
          tagColor:  tag.tagColor,
          // Champ custom : action recommandée par l'IA (sera affiché en plus)
          action:    ins.action || '',
        };
      })
    : undefined; // fallback aux mocks si vraiment 0 insights

  // weekTrends : 2 cartes potentielles
  //  - Carte "Mots les + cités" → top_keywords extraits des vrais avis Google/Revyo (back/IA)
  //  - Carte "Tendances IA"     → trends temporelles (déjà présentes)
  // Si AUCUNE des deux n'a de matière → tableau VIDE (pas undefined) pour
  // masquer toute la section et NE PAS afficher les mocks par défaut.
  const weekTrendsCards = [];
  if (topKeywords.length > 0) {
    // label volontairement omis : TabInsights.jsx applique le fallback i18n
    // `insights.section_week_words_title` quand label est absent (single source of truth).
    weekTrendsCards.push({
      items: topKeywords.slice(0, 5),
    });
  }
  if (trends.length > 0) {
    weekTrendsCards.push({
      label: t('insights.trends_detected_label'),
      items: trends.slice(0, 5),
    });
  }
  // [] (et pas undefined) → section masquée par TabInsights, pas de fallback mock
  const weekTrends = weekTrendsCards;

  // timeline : pas encore branché côté backend (TODO Phase 3 : table monthly_insights)
  // → on passe array vide pour que la section soit masquée
  const timeline = [];

  return (
    <>
      {error && (
        <div style={{
          background: '#FEF3C7', border: '1px solid #F5DBA0',
          borderRadius: 12, padding: '12px 16px', fontSize: 13,
          color: '#92580A', marginBottom: 16,
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* ===== Carte hero noire : insights réels du restaurant connecté ===== */}
      <div style={{
        background: 'linear-gradient(135deg, #18160F 0%, #2A2418 100%)',
        color: '#FFFFFF',
        borderRadius: 18,
        padding: '24px 28px',
        marginBottom: 20,
        boxShadow: '0 12px 40px rgba(24,22,15,0.18)',
      }}>
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#F59E0B', marginBottom: 6 }}>
            ✨ Insights IA
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: '#FFFFFF', letterSpacing: '-0.4px' }}>
            {restaurant?.nom
              ? t('insights.hero_title_named', { nom: restaurant.nom })
              : t('insights.hero_title_default')}
          </h2>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', margin: '4px 0 0' }}>
            {meta?.reviews_analyzed
              ? (meta.time_range_days
                  ? t('insights.hero_subtitle_with_days', { count: meta.reviews_analyzed, days: meta.time_range_days })
                  : t('insights.hero_subtitle', { count: meta.reviews_analyzed }))
              : t('insights.loading')}
            {restaurant?.ville && ` · ${restaurant.ville}`}
          </p>
        </div>

        {loading && (
          <div style={{ padding: '20px 0', color: 'rgba(255,255,255,0.6)', fontSize: 14, textAlign: 'center' }}>
            🌿 {t('insights.loading')}
          </div>
        )}

        {!loading && realInsights.length === 0 && (
          <div style={{
            padding: '20px',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 12,
            color: 'rgba(255,255,255,0.7)',
            fontSize: 13,
            lineHeight: 1.6,
            textAlign: 'center',
          }}>
            {t('insights.empty_state_line1')}<br />
            {t('insights.empty_state_line2')}
          </div>
        )}

        {!loading && realInsights.length > 0 && (() => {
          // Cohérence hero ↔ détail "Analyses par thématique" : on réordonne
          // pour montrer les unlocked d'abord, puis les locked. Sinon le hero
          // (slice 0..3 dans l'ordre d'origine) peut afficher une catégorie
          // verrouillée pendant que le détail (filter(!locked)) affiche la
          // même catégorie débloquée → impression d'incohérence pour l'user.
          // En tier free on tire 4 cartes (3 unlocked + 1 teaser hook) ; en
          // full il n'y a pas de locked donc slice(0,3) reste équivalent.
          const orderedInsights = [
            ...realInsights.filter(i => !i.locked),
            ...realInsights.filter(i => i.locked),
          ];
          const heroInsights = isFreeTier ? orderedInsights.slice(0, 4) : orderedInsights.slice(0, 3);
          return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            {heroInsights.map((ins, i) => {
              // Carte locked (free tier, cartes 4+ — peuvent apparaître dans la slice(0,3)
              // seulement si <3 unlocked, mais on garde le rendu défensif).
              if (ins.locked) {
                return (
                  <div key={ins.id || i} style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px dashed rgba(255,255,255,0.25)',
                    borderRadius: 12,
                    padding: '14px 16px',
                    color: 'rgba(255,255,255,0.85)',
                    position: 'relative',
                  }}>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                      textTransform: 'uppercase', color: '#F59E0B', marginBottom: 8,
                    }}>
                      <span style={{ fontSize: 14 }}>🔒</span>
                      {categoryLabel(ins.category, t) || t('insights.locked_card_title')}
                    </div>
                    <p style={{
                      fontSize: 13, lineHeight: 1.55, margin: '0 0 10px',
                      color: 'rgba(255,255,255,0.55)',
                      filter: 'blur(0.3px)',
                      fontStyle: 'italic',
                    }}>
                      {ins.teaser || t('insights.locked_teaser_default')}
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate('/abonnement')}
                      style={{
                        background: '#F59E0B',
                        color: '#18160F',
                        border: 'none',
                        borderRadius: 8,
                        padding: '7px 12px',
                        fontWeight: 700,
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      {t('insights.locked_unlock_all_cta')}
                    </button>
                  </div>
                );
              }

              const s = insightHeroStyle(ins.type);
              return (
                <div key={ins.id || i} style={{
                  background: s.bg,
                  border: `1px solid ${s.border}`,
                  borderRadius: 12,
                  padding: '14px 16px',
                  color: '#18160F',
                }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: s.tag, marginBottom: 8,
                  }}>
                    <span style={{ fontSize: 14 }}>{s.emoji}</span>
                    {categoryLabel(ins.category, t) || s.label}
                  </div>
                  <p style={{ fontSize: 13.5, lineHeight: 1.55, margin: '0 0 6px', color: '#18160F', fontWeight: 600 }}>
                    {ins.constat || ins.titre || ''}
                  </p>
                  {ins.action ? (
                    <p style={{ fontSize: 12, lineHeight: 1.45, margin: 0, color: '#6A6858', fontStyle: 'italic' }}>
                      → {ins.action}
                    </p>
                  ) : isFreeTier ? (
                    // Carte unlocked free : pas d'`action` côté backend → hook Pro.
                    <button
                      type="button"
                      onClick={() => navigate('/abonnement')}
                      style={{
                        background: 'transparent',
                        color: '#E8502A',
                        border: 'none',
                        padding: 0,
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      }}
                    >
                      {t('insights.locked_action_cta')}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
          );
        })()}

        {/* Bandeau récap "X insights verrouillés" si free + des locked existent */}
        {!loading && isFreeTier && meta?.locked_count > 0 && (
          <div style={{
            marginTop: 14,
            padding: '12px 14px',
            background: 'rgba(245,158,11,0.10)',
            border: '1px solid rgba(245,158,11,0.35)',
            borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            color: 'rgba(255,255,255,0.92)',
            fontSize: 13,
          }}>
            <span>
              🔒 <strong>{meta.locked_count}</strong> {t('insights.locked_banner_text', { count: meta.locked_count })}
            </span>
            <button
              type="button"
              onClick={() => navigate('/abonnement')}
              style={{
                background: '#F59E0B',
                color: '#18160F',
                border: 'none',
                borderRadius: 8,
                padding: '7px 12px',
                fontWeight: 700,
                fontSize: 12,
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              {t('insights.locked_banner_cta')}
            </button>
          </div>
        )}
      </div>

      <TabInsights
        period={period}
        themes={themes}
        timeline={timeline}
        weekTrends={weekTrends}
        onPeriodChange={handlePeriodChange}
        onRefresh={handleRefresh}
        onThemeClick={handleThemeClick}
      />
    </>
  );
}
